(() => {
  const invalidMessage = "We couldn't find an order matching those details. Please check your order number and mobile number.";
  const allowedTones = new Set(["neutral", "progress", "success", "warning", "critical"]);

  function make(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  }

  function safeUrl(value, allowedHosts) {
    try {
      const url = new URL(String(value || ""));
      if (url.protocol !== "https:") return null;
      if (allowedHosts && !allowedHosts.has(url.hostname)) return null;
      return url.toString();
    } catch {
      return null;
    }
  }

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-PK", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "Asia/Karachi",
    }).format(date);
  }

  function formatDateTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-PK", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Asia/Karachi",
    }).format(date);
  }

  function statusBadge(status) {
    const tone = allowedTones.has(status?.tone) ? status.tone : "neutral";
    return make("span", `razzi-status razzi-status--${tone}`, status?.label || "Order update");
  }

  function renderSummary(data) {
    const summary = make("div", "razzi-result__summary");
    const identity = make("div");
    identity.append(
      make("p", "razzi-result__order-label", "Order"),
      make("h2", "razzi-result__order-number", data.order.number),
    );
    const placedParts = [`Placed ${formatDate(data.order.placedAt)}`];
    if (data.order.city) placedParts.push(data.order.city);
    if (data.order.partial) placedParts.push("Partial shipment");
    identity.append(make("p", "razzi-result__placed", placedParts.filter(Boolean).join(" · ")));

    const current = make("div", "razzi-result__summary-status");
    current.append(statusBadge(data.status));
    current.append(make("p", "razzi-result__status-copy", data.status.description));
    summary.append(identity, current);
    return summary;
  }

  function renderProgress(progress) {
    const section = make("section", "razzi-result__section");
    section.append(make("h2", "razzi-result__section-title", "Tracking progress"));
    const list = make("ol", "razzi-progress");
    for (const stage of progress || []) {
      const item = make("li", "razzi-progress__step");
      item.dataset.state = stage.state || "upcoming";
      item.append(
        make("span", "razzi-progress__dot"),
        make("span", "razzi-progress__label", stage.label),
      );
      list.append(item);
    }
    section.append(list);
    return section;
  }

  function trackingValue(shipment) {
    const wrapper = make("p", "razzi-parcel__tracking");
    wrapper.append(document.createTextNode("Tracking: "));
    const trackingUrl = safeUrl(shipment.trackingUrl);
    if (trackingUrl && shipment.trackingNumber) {
      const link = make("a", "", shipment.trackingNumber);
      link.href = trackingUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      wrapper.append(link);
    } else {
      wrapper.append(document.createTextNode(shipment.trackingNumber || "Awaiting assignment"));
    }
    return wrapper;
  }

  function renderHistory(events) {
    if (!events?.length) return null;
    const details = make("details", "razzi-history");
    details.append(make("summary", "", "Tracking history"));
    const list = make("ol", "razzi-history__list");
    for (const event of events) {
      const item = make("li", "razzi-history__item");
      item.append(make("p", "", event.description || event.status));
      const meta = [formatDateTime(event.timestamp), event.location].filter(Boolean).join(" · ");
      if (meta) item.append(make("small", "", meta));
      list.append(item);
    }
    details.append(list);
    return details;
  }

  function renderShipments(shipments) {
    if (!shipments?.length) return null;
    const section = make("section", "razzi-result__section");
    section.append(make("h2", "razzi-result__section-title", shipments.length > 1 ? "Parcels" : "Shipment"));
    const parcels = make("div", "razzi-parcels");
    for (const shipment of shipments) {
      const parcel = make("article", "razzi-parcel");
      const head = make("div", "razzi-parcel__head");
      const identity = make("div");
      identity.append(
        make("p", "razzi-result__meta-label", "Courier"),
        make("p", "razzi-parcel__courier", shipment.courier),
        trackingValue(shipment),
      );
      head.append(identity, statusBadge(shipment.status));
      parcel.append(head);

      if (shipment.latestUpdate) {
        const latest = make("div", "razzi-parcel__latest");
        latest.append(make("p", "", shipment.latestUpdate.description));
        const latestMeta = [
          formatDateTime(shipment.latestUpdate.timestamp),
          shipment.latestUpdate.location,
        ].filter(Boolean).join(" · ");
        if (latestMeta) latest.append(make("small", "", latestMeta));
        parcel.append(latest);
      }
      const history = renderHistory(shipment.events);
      if (history) parcel.append(history);
      parcels.append(parcel);
    }
    section.append(parcels);
    return section;
  }

  function renderItems(items) {
    if (!items?.length) return null;
    const section = make("section", "razzi-result__section");
    section.append(make("h2", "razzi-result__section-title", "Items in this order"));
    const list = make("div", "razzi-items");
    for (const product of items) {
      const item = make("article", "razzi-item");
      const imageBox = make("div", "razzi-item__image");
      const imageUrl = safeUrl(product.imageUrl);
      if (imageUrl) {
        const image = make("img");
        image.src = imageUrl;
        image.alt = product.imageAlt || product.title || "Order item";
        image.loading = "lazy";
        image.width = 144;
        image.height = 144;
        imageBox.append(image);
      } else {
        imageBox.append(make("span", "razzi-item__placeholder", "RD"));
      }
      const copy = make("div");
      copy.append(make("p", "razzi-item__title", product.title));
      if (product.variant && product.variant !== "Default Title") {
        copy.append(make("p", "razzi-item__variant", product.variant));
      }
      item.append(imageBox, copy, make("p", "razzi-item__quantity", `Qty ${product.quantity}`));
      list.append(item);
    }
    section.append(list);
    return section;
  }

  function renderSupport(support) {
    const url = safeUrl(support?.whatsappUrl, new Set(["wa.me", "api.whatsapp.com"]));
    if (!url) return null;
    const supportBox = make("section", "razzi-support");
    const copy = make("div");
    copy.append(
      make("h2", "", "Need help with your order?"),
      make("p", "", "Our team is available on WhatsApp."),
    );
    const link = make("a", "razzi-support__button", "Contact us on WhatsApp");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    supportBox.append(copy, link);
    return supportBox;
  }

  class RazziOrderTracker extends HTMLElement {
    connectedCallback() {
      this._form = this.querySelector("[data-tracking-form]");
      this._message = this.querySelector("[data-message]");
      this._result = this.querySelector("[data-result]");
      this._submit = this._form?.querySelector("button[type='submit']");
      this._spinner = this.querySelector("[data-spinner]");
      this._buttonLabel = this.querySelector("[data-button-label]");
      this._form?.addEventListener("submit", (event) => this.track(event));
    }

    setLoading(loading) {
      if (this._submit) this._submit.disabled = loading;
      if (this._spinner) this._spinner.hidden = !loading;
      if (this._buttonLabel) this._buttonLabel.textContent = loading ? "Checking order" : "Track order";
      this.setAttribute("aria-busy", String(loading));
    }

    showMessage(message) {
      if (!this._message) return;
      this._message.textContent = message;
      this._message.hidden = false;
      this._result.hidden = true;
    }

    render(data) {
      this._message.hidden = true;
      const content = document.createDocumentFragment();
      content.append(renderSummary(data));
      if (data.notice) content.append(make("div", "razzi-tracker__notice", data.notice));
      content.append(renderProgress(data.progress));
      const shipments = renderShipments(data.shipments);
      const items = renderItems(data.items);
      const support = renderSupport(data.support);
      if (shipments) content.append(shipments);
      if (items) content.append(items);
      if (support) content.append(support);
      this._result.replaceChildren(content);
      this._result.hidden = false;
      this._result.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    async track(event) {
      event.preventDefault();
      const formData = new FormData(this._form);
      const payload = {
        orderNumber: String(formData.get("orderNumber") || "").trim(),
        mobile: String(formData.get("mobile") || "").trim(),
        company: String(formData.get("company") || ""),
      };
      if (!payload.orderNumber || !payload.mobile) {
        this.showMessage(invalidMessage);
        return;
      }
      const endpoint = safeUrl(this.dataset.endpoint);
      if (!endpoint) {
        this.showMessage("Tracking is temporarily unavailable. Please try again shortly.");
        return;
      }
      this.setLoading(true);
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "omit",
          cache: "no-store",
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok) {
          this.showMessage(data?.message || invalidMessage);
          return;
        }
        this.render(data);
      } catch {
        this.showMessage("Tracking is temporarily unavailable. Please try again shortly.");
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get("razzi-order-tracker")) {
    customElements.define("razzi-order-tracker", RazziOrderTracker);
  }
  document.querySelectorAll("razzi-order-tracker").forEach((tracker) => {
    if (!tracker._form && typeof tracker.connectedCallback === "function") tracker.connectedCallback();
  });
})();
